import { useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Field, KlantSelect, PRIOS, AANVRAAG_BADGE, fmtDate, klantNaamFrom } from "./salesShared";
import { Plus, Trash2, Camera, X, Loader2, Pencil } from "lucide-react";

const empty = { klantCode: "", productnaam: "", omschrijving: "", eisen: "", referentie: "", gewensteDatum: "", prioriteit: "Normaal", status: "Nieuw", fotos: [] };

export default function AanvragenTab({ klanten, aanvragen, reload, user }) {
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem("clazing_token");
  const klantNaam = (c) => klantNaamFrom(klanten, c);

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of files) { const fd = new FormData(); fd.append("file", f, f.name || "foto.jpg"); const r = await api.post("/upload", fd); added.push(r.data.path); }
      setForm((s) => ({ ...s, fotos: [...(s.fotos || []), ...added] }));
    } catch (err) { toast.error("Upload mislukt: " + formatApiError(err)); }
    setUploading(false); if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    if (!form.productnaam.trim()) return toast.error("Gewenst product is verplicht.");
    const body = { ...form, klantNaam: klantNaam(form.klantCode), aangevraagdDoor: user?.name };
    try {
      if (editId) {
        await api.put(`/sales/productaanvragen/${editId}`, body);
        toast.success("Productaanvraag bijgewerkt");
      } else {
        await api.post("/sales/productaanvragen", body);
        toast.success("Productaanvraag ingediend");
      }
      setForm(empty);
      setEditId(null);
      reload();
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (aanvraag) => {
    setEditId(aanvraag.id);
    setForm({ ...empty, ...aanvraag });
  };
  const cancelEdit = () => {
    setEditId(null);
    setForm(empty);
  };
  const del = async (a) => { if (!window.confirm("Aanvraag verwijderen?")) return; try { await api.delete(`/sales/productaanvragen/${a.id}`); reload(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-slate-800">
        Productaanvragen — nieuw artikel
      </h2>
      <p className="text-sm text-slate-500">
        Vraag een nieuw te ontwikkelen artikel aan met de eisen en foto's. De bedrijfsleiding
        neemt dit over in de Productontwikkeling-module en zet hier de voortgang/status.
      </p>
      {editId && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
          data-testid="aanvraag-edit-notice"
        >
          Je bewerkt een bestaande productaanvraag. Opslaan werkt deze aanvraag bij;
          er wordt geen nieuwe aanvraag aangemaakt.
        </div>
      )}
      <div className="rounded-lg border border-slate-200 bg-white p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Klant"><KlantSelect klanten={klanten} value={form.klantCode} onChange={(v) => setForm({ ...form, klantCode: v })} testid="aanvraag-klant" /></Field>
        <Field label="Gewenst product"><Input data-testid="aanvraag-product" value={form.productnaam} onChange={(e) => setForm({ ...form, productnaam: e.target.value })} /></Field>
        <Field label="Prioriteit"><Select value={form.prioriteit} onValueChange={(v) => setForm({ ...form, prioriteit: v })}><SelectTrigger data-testid="aanvraag-prio"><SelectValue /></SelectTrigger><SelectContent>{PRIOS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></Field>
        <Field label="Gewenste datum"><Input type="date" data-testid="aanvraag-datum" value={form.gewensteDatum} onChange={(e) => setForm({ ...form, gewensteDatum: e.target.value })} /></Field>
        <Field label="Referentie / voorbeeld"><Input data-testid="aanvraag-referentie" value={form.referentie} onChange={(e) => setForm({ ...form, referentie: e.target.value })} /></Field>
        <div className="hidden lg:block" />
        <div className="sm:col-span-2 lg:col-span-3"><Field label="Omschrijving"><Textarea data-testid="aanvraag-omschrijving" rows={2} value={form.omschrijving} onChange={(e) => setForm({ ...form, omschrijving: e.target.value })} /></Field></div>
        <div className="sm:col-span-2 lg:col-span-3"><Field label="Waar moet het aan voldoen? (eisen / specificaties)"><Textarea data-testid="aanvraag-eisen" rows={3} value={form.eisen} onChange={(e) => setForm({ ...form, eisen: e.target.value })} placeholder="Bijv. gewicht, coating, smaak, verpakking, allergenen, THT, prijsdoel…" /></Field></div>
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Foto's</label>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="aanvraag-photo-input" onChange={onPhotos} />
          <button type="button" data-testid="aanvraag-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}Foto's toevoegen{(form.fotos || []).length ? ` (${form.fotos.length})` : ""}
          </button>
          {(form.fotos || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {form.fotos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={`${API}/files/${p}?auth=${token}`} alt={`foto ${i + 1}`} className="h-16 w-16 object-cover rounded border border-slate-200" />
                  <button
                    type="button"
                    data-testid="aanvraag-photo-remove"
                    onClick={() => setForm((s) => ({
                      ...s,
                      fotos: s.fotos.filter((_, j) => j !== i),
                    }))}
                    className="absolute -top-1.5 -right-1.5 rounded-full bg-rose-500 p-0.5 text-white"
                    title={`Foto ${i + 1} verwijderen`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="aanvraag-save"
            onClick={save}
            className="bg-[#1C64B0] hover:bg-[#154E8A]"
          >
            {editId ? <Pencil className="mr-1 h-4 w-4" /> : <Plus className="mr-1 h-4 w-4" />}
            {editId ? "Aanvraag bijwerken" : "Aanvraag indienen"}
          </Button>
          {editId && (
            <Button
              variant="outline"
              data-testid="aanvraag-edit-cancel"
              onClick={cancelEdit}
            >
              Annuleren
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {aanvragen.length === 0 && <p className="text-sm text-slate-400">Nog geen productaanvragen.</p>}
        {aanvragen.map((a) => (
          <div key={a.id} data-testid="aanvraag-row" className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-800">{a.productnaam} <span className="text-xs font-normal text-slate-400">· {a.klantNaam || a.klantCode || "—"}</span></div>
              <div className="flex items-center gap-2">
                <Badge className={AANVRAAG_BADGE[a.status] || "bg-amber-100 text-amber-700"}>{a.status || "Nieuw"}</Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  data-testid="aanvraag-edit"
                  title="Productaanvraag bewerken"
                  onClick={() => edit(a)}
                >
                  <Pencil className="h-4 w-4 text-slate-500" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  data-testid="aanvraag-delete"
                  title="Productaanvraag verwijderen"
                  onClick={() => del(a)}
                >
                  <Trash2 className="h-4 w-4 text-slate-400" />
                </Button>
              </div>
            </div>
            <div className="text-xs text-slate-500 mt-1">{a.gewensteDatum && <span>Gewenst: {fmtDate(a.gewensteDatum)} · </span>}Prioriteit: {a.prioriteit}{a.referentie ? ` · Ref: ${a.referentie}` : ""}{a.aangevraagdDoor ? ` · door ${a.aangevraagdDoor}` : ""}</div>
            {a.omschrijving && <p className="text-sm text-slate-600 mt-1">{a.omschrijving}</p>}
            {a.eisen && <p className="text-sm text-slate-700 mt-1"><b>Eisen:</b> {a.eisen}</p>}
            {(a.fotos || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {a.fotos.map((p, i) => (
                  <a key={i} href={`${API}/files/${p}?auth=${token}`} target="_blank" rel="noreferrer">
                    <img src={`${API}/files/${p}?auth=${token}`} alt={`foto ${i + 1}`} className="h-16 w-16 object-cover rounded border border-slate-200" />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
